import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Pin, Lock, EyeOff, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { isStaff } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import SteamLoginButton from "@/components/SteamLoginButton";

function formatDate(iso) {
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return ""; }
}

export default function ForumCategoryPage() {
  const { slug } = useParams();
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["forum-cat", slug],
    queryFn: () => api.get(`/forum/categories/${slug}/topics`).then((r) => r.data),
  });

  const cat = data?.category;
  const topics = data?.topics || [];
  const canCreate = user && (cat?.post_permission === "member" || (cat?.post_permission === "staff" && isStaff(user)));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <div className="mb-2 text-sm text-muted-foreground">
        <Link to="/forum" className="hover:text-primary">← Forum</Link>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">{cat?.name || slug}</h1>
          {cat && <p className="text-muted-foreground mt-1">{cat.description}</p>}
          {cat?.visibility === "private_to_author" && (
            <Badge variant="outline" className="mt-2 border-primary/40 text-primary"><Lock className="h-3 w-3 mr-1" /> Sujets privés (auteur & staff)</Badge>
          )}
        </div>
        {user ? (
          canCreate ? (
            <Button asChild data-testid="new-topic-button">
              <Link to={`/forum/c/${slug}/nouveau`}><Plus className="h-4 w-4 mr-1" /> Nouveau sujet</Link>
            </Button>
          ) : (
            <Badge variant="outline" className="border-muted-foreground/50">Création réservée au staff</Badge>
          )
        ) : (
          <SteamLoginButton label="Se connecter pour poster" />
        )}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Chargement...</p>
      ) : topics.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground">Aucun sujet pour le moment. Soyez le premier à en créer un.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {topics.map((t) => (
            <Link key={t.id} to={`/forum/t/${t.id}`} data-testid={`topic-item-${t.id}`}>
              <Card className="hover:border-primary/60 transition-colors">
                <CardContent className="p-4 flex items-center gap-4">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={t.author.avatar_url} />
                    <AvatarFallback>{t.author.persona_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {t.pinned && <Pin className="h-3.5 w-3.5 text-primary" />}
                      {t.locked && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                      {t.hidden && <EyeOff className="h-3.5 w-3.5 text-destructive" />}
                      <p className="font-medium truncate">{t.title}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      par <span className="text-foreground/80">{t.author.persona_name}</span> · {formatDate(t.created_at)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono-tech text-lg text-primary">{t.reply_count}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">réponses</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
